/* eslint-disable react-hooks/exhaustive-deps */
import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  addAppealCouncilLitigation,
  addAppealLitigation,
  fetchAppealDecisionMeasureTypes,
  fetchAppealProsecutorDecisionTypes,
} from "services/prosecutorDecipilineService";
import { Complaints, Litigation } from "types";
import { LoadingIcon } from "utils/constant";
import { PROSECUTOR_APPEAL_OPINION } from "utils/constantValues";
import "../../complaintRequest/styles/AddEvidenceModal.css";
import { ROLES } from "utils/roles";
import { useHasRole } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";

interface DecisionModalProps {
  fromCourt?: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  litigationData: Litigation;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddAppealLitigationDecisionModal({
  fromCourt = false,
  isOpen,
  setIsOpen,
  litigationData,
  complaintData,
  onRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState("");

  const isRegionalCouncilOfficeHead = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);

  const [formState, setFormState] = useState({
    files: [] as File[],
    council_decision_summary: "",
    council_decision_day: "",
    prosecutor_opinion_on_decision: "",
    council_decision_type: "",
    discipline_measure_type: "",
    prosecutor_opinion_on_decision_appeal:
      PROSECUTOR_APPEAL_OPINION?.NOT_APPEAL,
  });
  const [decisionTypes, setDecisionTypes] = useState<any>([]);
  const [decisionMeasureTypes, setDecisionMeasureTypes] = useState<any>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDecisionTypes = useCallback(async () => {
    try {
      const resData = await fetchAppealProsecutorDecisionTypes();
      if (resData) {
        setDecisionTypes(resData);
      }
    } catch (err) {
      // setError("Failed to load investigation.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDecisionMeasureTypes = useCallback(async () => {
    try {
      const resData = await fetchAppealDecisionMeasureTypes();
      if (resData) {
        setDecisionMeasureTypes(resData);
      }
    } catch (err) {
      // setError("Failed to load investigation.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDecisionTypes();
    loadDecisionMeasureTypes();
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!litigationData) return;

    setLoading(true);

    const data = new FormData();
    data.append("council_decision_files", formState?.files[0] || "");
    data.append("council_decision_summary", formState.council_decision_summary);
    data.append("council_decision_day", formState.council_decision_day);
    data.append("council_decision_type", formState.council_decision_type);
    data.append("discipline_measure_type", formState.discipline_measure_type);
    data.append(
      "prosecutor_opinion_on_decision",
      formState.prosecutor_opinion_on_decision,
    );
    data.append(
      "prosecutor_opinion_on_decision_appeal",
      formState?.prosecutor_opinion_on_decision_appeal,
    );
    data.append(
      "is_final_verdict_given",
      String(litigationData?.is_final_verdict_given),
    );

    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_org_id",
      complaintData?.accused_prosecutor_org_id || "",
    );
    data.append(
      "accused_prosecutor_admin_unit_id",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );

    litigationData?.summon &&
      data.append("summon", String(litigationData?.summon));

    litigationData?.summonappeal_accusepProsecutor &&
      data.append(
        "summonappeal_accusepProsecutor",
        String(litigationData?.summonappeal_accusepProsecutor),
      );
    litigationData?.appealto_court &&
      data.append("appealto_court", String(litigationData?.appealto_court));

    litigationData?.appeal_request &&
      data.append("appeal_request", String(litigationData?.appeal_request));

    // const litigationId = litigationData?.id || "";
    const isFormData = true;

    try {
      await (isRegionalCouncilOfficeHead
        ? addAppealCouncilLitigation(data, isFormData)
        : addAppealLitigation(data, isFormData));

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        files: [] as File[],
        council_decision_summary: "",
        council_decision_day: "",
        prosecutor_opinion_on_decision: "",
        council_decision_type: "",
        discipline_measure_type: "",
        prosecutor_opinion_on_decision_appeal:
          PROSECUTOR_APPEAL_OPINION?.NOT_APPEAL,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        isRegionalCouncilOfficeHead
          ? t("addCouncilDecision")
          : t("addProsecutorDecision")
      }
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          {!isRegionalCouncilOfficeHead && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                {t("prosecutorOpinionOnAppeal")}
              </label>
              <select
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                name="prosecutor_opinion_on_decision_appeal"
                value={formState.prosecutor_opinion_on_decision_appeal}
                onChange={handleChange}
              >
                {Object.values(PROSECUTOR_APPEAL_OPINION)?.map((list) => (
                  <option key={list} value={list}>
                    {list}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!isRegionalCouncilOfficeHead && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("prosecutorOpinionOnDecision")}
              </label>
              <textarea
                rows={3}
                name="prosecutor_opinion_on_decision"
                value={formState.prosecutor_opinion_on_decision}
                onChange={handleChange}
                placeholder={t("detailsHere")}
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {fromCourt ? t("courtDecisionDate") : t("councilDecisionDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="council_decision_day"
              placeholder={t("egJune122014")}
              value={formState.council_decision_day}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {fromCourt ? t("courtDecisionType") : t("councilDecisionType")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="council_decision_type"
              value={formState.council_decision_type}
              onChange={handleChange}
            >
              <option value="">{t("selectDisciplineType")}</option>
              {decisionTypes?.map((list: any) => (
                <option key={list.id} value={list.name}>
                  {list.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("decisionMeasureType")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="discipline_measure_type"
              value={formState.discipline_measure_type}
              onChange={handleChange}
            >
              <option value="">{t("selectDisciplineMeasure")}</option>
              {decisionMeasureTypes?.map((list: any) => (
                <option key={list.id} value={list.measure}>
                  {list.measure}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("summary")}
            </label>
            <textarea
              required
              rows={3}
              name="council_decision_summary"
              value={formState.council_decision_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {fromCourt ? t("courtDecisionFile") : t("councilDecisionFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState.files.length > 0 && (
              <div className="file-list">
                {formState.files.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}...
            </div>
          ) : (
            t("addDecision")
          )}
        </button>
      </form>
    </SideModal>
  );
}
